import { OrderDrawerIdentifier } from "@features/orders-features/components/order-drawer/types";
import { useClaims } from "../../../../../../../auth/ClaimsProvider";
import {
  Card,
  CardActionArea,
  Collapse,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useId, useMemo, useState } from "react";
import { ApartmentOutlined, LocationOn } from "@mui/icons-material";
import { KeyValue } from "@features/orders-features/types/commonTypes";
import { useGetBillTo } from "@features/orders-features/hooks/useGetBillTo";

function getIcon(key: string) {
  if (key === "recipient") {
    return (
      <ApartmentOutlined
        fontSize="small"
        color="action"
        sx={{ mr: 1 }}
        aria-hidden="true"
      />
    );
  }
  if (key.includes("addr-")) {
    return (
      <LocationOn
        fontSize="small"
        color="action"
        sx={{ mr: 1, visibility: key === "addr-1" ? "visible" : "hidden" }}
        aria-hidden="true"
      />
    );
  }
  return null;
}

export function BillToContent({ data }: { data: Array<KeyValue> }) {
  return (
    <Stack direction="column" spacing={1} sx={{ px: 2, py: 2 }}>
      {data.map((item) => (
        <Typography
          key={item.key}
          variant="body1"
          sx={{ display: "flex", alignItems: "center" }}
        >
          {getIcon(item.key)}
          {item.value}
        </Typography>
      ))}
    </Stack>
  );
}

export function OrderDrawerBillTo({
  biOrderId,
  webOrderId,
  orgUnitId,
  disableGutters = false,
}: OrderDrawerIdentifier & {
  disableGutters?: boolean;
}) {
  const { getClaimValue } = useClaims();
  const [expanded, setExpanded] = useState(true);
  const billToSectionId = useId();
  const billToPanelId = useId();

  const orgUnitIdCurrent = Number(getClaimValue("orgUnitIdCurrent") ?? null);

  const { data, isLoading, isError } = useGetBillTo(
    biOrderId ?? undefined,
    webOrderId ?? undefined,
    orgUnitId ?? orgUnitIdCurrent,
  );

  const billTo = useMemo(() => {
    const a: Array<KeyValue> = [];

    if (!data) {
      return a;
    }

    const p = data;

    if (p.addressName) a.push({ key: "recipient", value: p.addressName });

    if (p.addressLine1) a.push({ key: "addr-1", value: p.addressLine1 });
    if (p.addressLine2) a.push({ key: "addr-2", value: p.addressLine2 });
    if (p.addressLine3) a.push({ key: "addr-3", value: p.addressLine3 });

    if (p.county) a.push({ key: "addr-county", value: p.county });

    const cityState = [p.city, p.stateProvince].filter(Boolean).join(", ");
    const cityPostal = [cityState, p.postalCode]
      .filter(Boolean)
      .join(" ")
      .trim();
    if (cityPostal) a.push({ key: "addr-cityStateZip", value: cityPostal });

    if (p.countryCode) a.push({ key: "addr-country", value: p.countryCode });
    else if (p.country) a.push({ key: "addr-country", value: p.country });

    return a;
  }, [data]);

  if (isLoading || isError || !billTo.length) {
    return null;
  }

  return (
    <Card variant="outlined" sx={{ mx: disableGutters ? 0 : 2, my: 0, borderRadius: 1 }}>
      <CardActionArea
        id={billToSectionId}
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
        aria-controls={billToPanelId}
        aria-label="Toggle billing address"
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 2,
          py: 1.5,
        }}
      >
        <Typography
          component="h3"
          variant="subtitle1"
          sx={{ textTransform: "uppercase" }}
        >
          Billing Address
        </Typography>
        <ExpandMoreIcon
          sx={{
            transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s",
            color: "action.active",
          }}
          aria-hidden="true"
        />
      </CardActionArea>
      <Collapse
        id={billToPanelId}
        in={expanded}
        role="region"
        aria-labelledby={billToSectionId}
      >
        <Divider sx={{ borderStyle: "solid" }} />
        <BillToContent data={billTo} />
      </Collapse>
    </Card>
  );
}
